<template>
  <div class="tw-bg-gray-200 q-my-md q-pa-md tw-rounded-md" v-show="!isFormHidden">
    <h2 class="text-h4 q-my-sm">{{ formName.replaceAll('_', ' ') }}</h2>
    <h4 v-if="props.product" class="text-h4">
      {{ t('recap.product', 0, { locale: recapLanguage }) }}:
      {{ product?.name }}
    </h4>
    <template
      v-for="fieldOrFieldBlock in fields"
      :key="`${recapType}-${taskOrStoreId}-${fieldOrFieldBlock.id}-${repeatingIndex}-${isFieldBlock(
        fieldOrFieldBlock
      )}`"
    >
      <template v-if="!isFieldBlock(fieldOrFieldBlock)">
        <recap-field
          :field="fieldOrFieldBlock"
          :repeating-index="repeatingIndex"
          :ref="(getFieldRef as any)"
          @value-change="mapData(fieldOrFieldBlock.id, repeatingIndex)"
        ></recap-field>
        <template v-if="props.form.form_type === 'survey'">
          <q-btn v-if="props.lastSurvey" push color="negative" @click="$emit('removeSurvey')">
            Remove Response
          </q-btn>
          <q-btn push color="primary" @click="$emit('addSurvey')" class="tw-ml-2">
            Add Response
          </q-btn>
        </template>
        <!-- <slot name="extraFields" /> -->
      </template>
    </template>
    <InventoryItemSelection
      v-if="inventoryItemSelection"
      :task="inventoryItemSelection.task"
      :task-properties="inventoryItemSelection.taskProperties"
      :inventory-item-selection="inventoryItemSelection.inventoryItemSelection"
      @update:inventory-item-selection="inventoryItemSelection.updateInventoryItem"
    />
  </div>
</template>

<script setup lang="ts">
import RecapField from './RecapField.vue'
import InventoryItemSelection from '../task_profile/InventoryItemSelection.vue'
import { ref, computed, inject, onMounted, watchEffect } from 'vue'
import { Form, LocaleStrings, Product } from '../../types/recap'
import { useDisplayLogic } from '../../composables/recaps/useDisplayLogic'
import { useLogic } from '../../composables/recaps/useLogic'
import { useRecapHelpers } from '../../composables/recaps/useRecapHelpers'
import { useI18n } from 'vue-i18n'
import { useSharedRecap } from '../../composables/recaps/useRecap'

const {
  getFieldRef,
  recapLanguage,
  recapId,
  taskOrStoreId,
  recapType,
  mapData,
  getAreAllFieldsForFormHidden,
} = useSharedRecap()

const props = defineProps<{
  form: Form
  product?: Product
  repeatingIndex?: number
  lastSurvey?: boolean
}>()

defineEmits(['addSurvey', 'removeSurvey'])

const { t } = useI18n()

// Fields
const fields = computed(() => props.form.items || props.form.fields || [])

// Helpers
const { isFieldBlock } = useRecapHelpers()

// Languages
const formName = computed(
  () => props.form.name_l10n[recapLanguage.value as keyof LocaleStrings] || props.form.name
)

// Display Logic
const { shouldBeHidden, handleAllDisplayLogic } = useDisplayLogic(
  recapId.value,
  taskOrStoreId,
  recapType
)
const isFormHidden = ref(false)
const logicHidesForm = ref(false)
watchEffect(() => {
  if (shouldBeHidden.value || logicHidesForm.value || getAreAllFieldsForFormHidden(props.form.id)) {
    isFormHidden.value = true
  } else {
    isFormHidden.value = false
  }
})

// inventory Item Selection
const inventoryItemSelection = inject<any>('inventoryItemSelection', false)

function getFormIsHidden() {
  return isFormHidden.value
}
function getFormId() {
  return props.form.id
}
function getFormType() {
  return props.form.form_type
}
function getProductsId() {
  return props.product?.id
}
function getLogicGroups() {
  return props.form.logic_groups
}
function getRepeatingIndex() {
  return props.repeatingIndex
}
function runLogic(selfRef: any, dependenceRefs: any[]) {
  const { checkLogicConditions } = useLogic(selfRef, dependenceRefs)
  const logicResult = checkLogicConditions(props.repeatingIndex)
  // Invert the result; the variable is for if the form *is* hidden by logic
  logicHidesForm.value = !logicResult
}
defineExpose({
  getFormIsHidden,
  getFormId,
  getFormType,
  getProductsId,
  getLogicGroups,
  getRepeatingIndex,
  runLogic,
})

onMounted(() => {
  // Run Display Logic
  handleAllDisplayLogic(props.form)
})
</script>
